import React, { useState, ChangeEvent, useEffect } from 'react';
import { Grid, TextField, FormHelperTextProps, Typography, Switch, InputAdornment, IconButton } from '@material-ui/core';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { useStyles } from './ConfigurationKsdo.styles';
import { useLayoutContext } from '../../../Context/Layout.context';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';
import {
  getKsdoConfiguration,
  updateKsdoConfiguration,
} from '../../../services/admin/settings/systemSettings.service';
import { IConfigurationKsdo } from './ConfigurationKsdo.types';
import { useTranslation } from 'react-i18next';
import { CancelButton } from '../../buttons/CancelButton.component';
import { ConfirmButton } from '../../buttons/ConfirmButton.component';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { getSyncTime } from '../ConfigurationApi/helpers';
import SwitchOne from '../../SwitchOne/SwitchOne.component';

const CLIENT_ID_MAX_LENGTH = 100;
const CLIENT_SECRET_MAX_LENGTH = 100;
const URL_MAX_LENGTH = 256;

interface ConfigurationKsdoProps {
  onChange: () => void;
  onClose: () => void;
}

export const ConfigurationKsdo: React.FC<ConfigurationKsdoProps> = ({ onChange, onClose }) => {
  const classes = useStyles();

  const { showLoader, openAlert } = useLayoutContext();
  const { t } = useTranslation();

  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [configurationKsdo, setConfigurationKsdo] = useState<IConfigurationKsdo>({} as IConfigurationKsdo)
  const [clientId, setClientId] = useState<string>(configurationKsdo.clientId ?? '');
  const [clientSecret, setClientSecret] = useState<string>(configurationKsdo.clientSecret ?? '');
  const [url, setUrl] = useState<string>(configurationKsdo.url ?? '');
  const [isKsdoLoginEnabled, setIsKsdoLoginEnabled] = useState<boolean>(configurationKsdo.isKSDOLoginEnabled ?? false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false)

  const [hasClientIdFocus, setHasClientIdFocus] = useState<boolean>(false);
  const [hasClientSecretFocus, setHasClientSecretFocus] = useState<boolean>(false);
  const [hasUrlFocus, setHasUrlFocus] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  const [showPasswordUrl, setShowPasswordUrl] = React.useState(false);
  const [showPasswordSecret, setShowPasswordSecret] = React.useState(false);

  const handleClickShowPasswordUrl = () => setShowPasswordUrl((show) => !show);

  const handleMouseDownPasswordUrl = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleClickShowPasswordSecret = () => setShowPasswordSecret((show) => !show);

  const handleMouseDownPasswordSecret = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const isClientIdTooLong = clientId.length > CLIENT_ID_MAX_LENGTH;
  const isClientSecretTooLong = clientSecret.length > CLIENT_SECRET_MAX_LENGTH;
  const isUrlTooLong = url.length > URL_MAX_LENGTH;

  const handleSuccess = () => {
    openAlert('success');
    onClose();
  };

  const handleError = (error: { response: { data: IConfigurationKsdo } }) => {
    openAlert('error');
  };

  useEffect(() => {
    showLoader(true);
    getKsdoConfiguration()
      .then((response: IConfigurationKsdo) => {
        setConfigurationKsdo(response);
        setClientId(response.clientId);
        setClientSecret(response.clientSecret);
        setIsKsdoLoginEnabled(response.isKSDOLoginEnabled);
        setUrl(response.url);
      })
      .finally(() => {
        showLoader(false);
      });
  }, []);

  const handleOnSave = () => {
    showLoader(true, 'Aktualizuję dane');
    updateKsdoConfiguration(
      isKsdoLoginEnabled,
      clientId,
      clientSecret,
      url,
    )
      .then(
        (result: IConfigurationKsdo) => {
          openAlert(
            'success',
            `Zapisano nową konfigurację. Zmiany zostaną wprowadzone w ciagu ${getSyncTime(result.syncTimeMinutes)}`,
          ); 
          setIsButtonDisabled(true)       
        },
        (error) => {
          handleError(error);
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const handleClientIdChange = (value: string) => {
    setClientId(value);
  };

  const handleClientSecretChange = (value: string) => {
    setClientSecret(value);
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
  };

  const handleIsKsdoLoginEnabledChange = (event: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
    setIsKsdoLoginEnabled(value);
  };

  useEffect(() => {
    setIsDataChanged(
      (clientId !== '' && clientSecret !== '' && url !== '') &&
      clientId !== configurationKsdo.clientId ||
      clientSecret !== configurationKsdo.clientSecret ||
      url !== configurationKsdo.url ||
      isKsdoLoginEnabled !== configurationKsdo.isKSDOLoginEnabled
    );
    setIsButtonDisabled(false);
  }, [
    clientId,
    clientSecret,
    url,
    isKsdoLoginEnabled,
    configurationKsdo,
  ]);

  const handleOnClose = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onClose();
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <div className={classes.listHeader}>
            <Typography className={classes.tabTitle}>{`Konfiguracja ${t('global:shortNames.KSDO')}`}</Typography>
          </div>
        </Grid>
          <Grid item xs={12} className={classes.switchGrid}>
            <Typography className={`${classes.subTitle} ${classes.switchTitle}`}>Pokaż przycisk zaloguj za pomocą {t('global:shortNames.KSDO')}</Typography>
            <SwitchOne
              disableRipple
              color="primary"
              checked={isKsdoLoginEnabled}
              onChange={handleIsKsdoLoginEnabledChange}
              inputProps={{ 'aria-label': 'Pokaż przycisk zaloguj za pomocą KSDO' }}
            />
          </Grid>
          <Grid item xs={12} className={classes.helperRow}></Grid>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Adres URL kierujący do strony logowania przez {t('global:shortNames.KSDO')}</Typography>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={url}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleUrlChange(e.target.value)}
              label="URL*"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  color: isUrlTooLong ? 'red' : 'black',
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onFocus={() => setHasUrlFocus(true)}
              onBlur={() => setHasUrlFocus(false)}
              error={isUrlTooLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={
                    isUrlTooLong
                      ? 'Podany adres URL jest za długi'
                      : ''
                  }
                  limitsVisible={hasUrlFocus || isUrlTooLong}
                  valueLength={url.length}
                  limit={URL_MAX_LENGTH}
                  withError={isUrlTooLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Uwierzytelnianie {t('global:shortNames.system')} w {t('global:shortNames.KSDO')}</Typography>
          </Grid>
          <Grid item xs={6} className={classes.item}>
          <TextField
              value={clientId}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleClientIdChange(e.target.value)}
              label="Client_ID*"
              variant="outlined"
              type={showPasswordUrl ? 'text' : 'password'}
              fullWidth
              inputProps={{
                style: {
                  color: isClientIdTooLong ? 'red' : 'black',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPasswordUrl}
                      onMouseDown={handleMouseDownPasswordUrl}
                    >
                      {showPasswordUrl ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onFocus={() => setHasClientIdFocus(true)}
              onBlur={() => setHasClientIdFocus(false)}
              error={isClientIdTooLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={
                    isClientIdTooLong
                      ? 'Podany numer Client_ID jest za długi'
                      : ''
                  }
                  limitsVisible={hasClientIdFocus || isClientIdTooLong}
                  valueLength={clientId.length}
                  limit={CLIENT_ID_MAX_LENGTH}
                  withError={isClientIdTooLong}
                />
              }
            />
          </Grid>
          <Grid item xs={6} className={classes.item}>
          <TextField
              value={clientSecret}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleClientSecretChange(e.target.value)}
              label="Client_Secret*"
              variant="outlined"
              type={showPasswordSecret ? 'text' : 'password'}
              fullWidth
              inputProps={{
                style: {
                  color: isClientSecretTooLong ? 'red' : 'black',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPasswordSecret}
                      onMouseDown={handleMouseDownPasswordSecret}
                    >
                      {showPasswordSecret ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onFocus={() => setHasClientSecretFocus(true)}
              onBlur={() => setHasClientSecretFocus(false)}
              error={isClientSecretTooLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={
                    isClientSecretTooLong
                      ? 'Podany numer Client_Secret jest za długi'
                      : ''
                  }
                  limitsVisible={hasClientSecretFocus || isClientSecretTooLong}
                  valueLength={clientSecret.length}
                  limit={CLIENT_SECRET_MAX_LENGTH}
                  withError={isClientSecretTooLong}
                />
              }
            />
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ textAlign: 'right', alignItems: 'end', marginTop: 140 }}>
        <CancelButton onClick={handleOnClose}>Anuluj</CancelButton>
        <ConfirmButton onClick={handleOnSave} disabled={isButtonDisabled || !clientId || !clientSecret || !url || !isDataChanged || isClientIdTooLong || isClientSecretTooLong || isUrlTooLong }>
          Zapisz
        </ConfirmButton>
      </Grid>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onClose} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
